import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NIGERIAN_STATES } from '@core/data/nigerian-states';
import { NotificationFacade } from '@core/facades/notification.facade';
import { AccountManagementService } from '@core/services/account-management.service';
import {
  AdminManagementService,
  ProfileDetails,
} from '@core/services/admin-management.service';
import { BVNProfileDetail, CommonService } from '@core/services/common.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { CustomValidators } from '@core/validators/custom-validator';
import { ButtonDirective } from '@shared/directives/button.directive';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import { debounceTime, distinctUntilChanged, filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-joint-account-email',
  standalone: true,
  imports: [
    ButtonDirective,
    CommonModule,
    InputComponent,
    InputLabelComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './joint-account-email.component.html',
  styleUrl: './joint-account-email.component.scss',
})
export class AccountEmailComponent implements OnInit, OnDestroy {
  @Output() profileData = new EventEmitter<ProfileDetails | null>();
  @Input() profileNumber!: 1 | 2 | 3;
  @Input() accountType!: "individual" | "joint"

  fb = inject(FormBuilder);
  adminService = inject(AdminManagementService);
  confirmDialogService = inject(ConfirmDialogService);
  accountService = inject(AccountManagementService);
  toast = inject(NotificationFacade);
  route = inject(ActivatedRoute);
  change = inject(ChangeDetectorRef);
  commonService = inject(CommonService);

  isValidating: boolean = false;
  subs: Subscription[] = [];
  profileDetails: ProfileDetails | null = null;
  email: string = '';
  showBvnField: boolean = false;
  description: string = '';

  emailForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, CustomValidators.strictEmailValidator()]],
    bvn: [''],
  });

  ngOnInit() {
    this.email = this.route.snapshot.queryParams['email'];
    if (this.email) {
      this.emailForm.patchValue({ email: this.email });
      this.checkEmailProfile(this.email);
    }
    this.formWatcher();
  }

  formWatcher() {
    // Watch email changes with debounce
    const emailSub = this.emailForm
      .get('email')!
      .valueChanges.pipe(
        debounceTime(1400),
        distinctUntilChanged(),
        filter((email) => this.emailForm.get('email')!.valid)
      )
      .subscribe((email) => {
        this.email = this.emailForm.getRawValue().email;
        this.emailForm.get('bvn')?.reset()
        this.checkEmailProfile(email);
        this.showBvnField = false;
        this.clearBvnValidators();
      });

    // Watch BVN changes with debounce
    const bvnSub = this.emailForm
      .get('bvn')!
      .valueChanges.pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        filter((bvn) => !!bvn && this.emailForm.get('bvn')!.valid)
      )
      .subscribe((bvn) => {
        this.validateBvn(bvn);
      });

    this.subs.push(emailSub, bvnSub);
  }

  checkEmailProfile(email: string) {
    this.isValidating = true;
    this.change.detectChanges()
    const sub = this.adminService.getProfileDetails(email).subscribe({
      next: (response) => {
        this.isValidating = false;
        // if profile exist
        if (response.data) {
          this.profileDetails = this.formatProfileDetails(response.data);
          this.profileDetails.email = this.email;
          this.getProfilesJointAccounts(this.profileDetails.id!);
          this.change.detectChanges();
        } else {
          this.showBvnField = true;
          // profile doesn't exist
          this.setBvnValidators();
          this.isValidating = false;
        }
      },
    });
  }

  validateBvn(bvn: string) {
    this.isValidating = true;
    this.change.detectChanges();
    const sub = this.commonService.validateUserDetails({ bvn: bvn }).subscribe({
      next: (response) => {
        // get the dojah bvn data
        const profileSub = this.commonService.getProfileDetails(bvn).subscribe({
          next: (response) => {
            this.isValidating = false;
            this.profileDetails = this.formatBVNProfileDetails(response.data);
            this.description = `Are you sure you want to create ${this.accountType === 'individual' ? 'an' : 'a'} ${this.accountType} account for ${this.email}?`;
            this.change.detectChanges()
          },
          error: (error) => {
            this.isValidating = false;
            this.emailForm.get('bvn')?.setErrors({ serverError: error });
            this.change.detectChanges();
          },
        });
        this.subs.push(profileSub);
      },
      error: (error) => {
        // invalidate the form
        this.isValidating = false;
        this.emailForm.get('bvn')?.setErrors({ serverError: error });
        this.change.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  submitForm() {
    this.isValidating = true;
    this.change.detectChanges();

    // no description if there's no profile
    if (!this.description) {
      this.description = `Are you sure you want to create a ${this.accountType === 'individual' ? 'an' : 'a'} ${this.accountType} account for ${this.email}?`;
      this.profileDetails = { email: this.email };
    }

    if (this.emailForm.valid) {
      this.confirmDialogService.openDialog({
        isOpen: true,
        isDialogLoading: false,
        title: `Yes, create ${this.accountType === 'individual' ? 'an' : 'a'} ${this.accountType} account`,
        description: this.description,
        loadingText: 'Creating account, please wait...',
        action: () => {
          this.closeDialog();
        },
        onClose: () => {
          this.isValidating = false;
        },
      });
    }
  }

  getProfilesJointAccounts(profileId: string) {
    const sub = this.accountService
      .getProfileJointAccounts(profileId)
      .subscribe({
        next: (response) => {
          // filter for joint account
          const jointAccountNames = response.data.map((acc: any) =>
            this.commonService.getJointAccountName(acc)
          );

          // create description for confirm dialog based in joint account count
          let actionText =
            this.profileNumber === 2
              ? `add ${this.email} to the joint account`
              : `create a ${this.accountType === 'individual' ? 'an' : 'a'} ${this.accountType} account for ${this.email}`;

          if (jointAccountNames.length > 0) {
            const formattedNames = jointAccountNames.join('\n');
            this.description = `Are you sure you want to ${actionText}?\n${this.email} already exists in ${formattedNames}`;
          } else {
            this.description = `Are you sure you want to ${actionText}?`;
          }
          this.change.detectChanges();
        },
        error: (error) => {
          this.toast.error('Failed to fetch account profiles', error);
        },
      });
    this.subs.push(sub);
  }

  formatProfileDetails(data: ProfileDetails): ProfileDetails {
    // Create a shallow copy so we don’t mutate the original object
    const formatted: any = { ...data };

    // 1. Title in uppercase
    if (formatted.title) {
      formatted.title = formatted.title.toUpperCase();
    }

    // 2. Replace null with empty string
    Object.keys(formatted).forEach((key) => {
      if (formatted[key] === null) {
        formatted[key] = '';
      }
    });

    // 3. Date → Date object
    if (formatted.dob) {
      formatted.dob = new Date(formatted.dob);
    }

    // 4. Political exposure boolean → string
    if (typeof formatted.political_exposure === 'boolean') {
      formatted.political_exposure = String(formatted.political_exposure);
    }

    // 5. State of origin → state_code
    if (formatted.state_of_origin) {
      const stateObj = NIGERIAN_STATES.find(
        (state) =>
          state.name.toLowerCase() === formatted.state_of_origin.toLowerCase()
      );
      if (stateObj) {
        formatted.state_of_origin = stateObj.state_code;
      }
    }

    return formatted;
  }

  formatBVNProfileDetails(data: BVNProfileDetail): ProfileDetails {
    return {
      id: data.id,
      title: data.title ?? '',
      last_name: data.last_name ?? '',
      first_name: data.first_name ?? '',
      middle_name: data.middle_name ?? '',
      marital_status: data.marital_status ?? '',
      state_of_origin: data.state_of_origin ?? '',
      nationality: data.nationality ?? '',
      dob: data.date_of_birth ?? '',
      gender: data.gender ?? '',
      phone_number_1: data.phone_number1 ?? '',
      phone_number_2: data.phone_number2 ?? '',
      bvn: data.identity_number ?? '',
      email: this.email, // set from current component state
    };
  }

  closeDialog() {
    this.confirmDialogService.updateDialog({
      isDialogLoading: false,
      isOpen: false,
    });
    this.isValidating = false;
    this.profileData.emit(this.profileDetails);
    console.log(this.profileDetails);
  }

  clearBvnValidators() {
    const bvnControl = this.emailForm.get('bvn')!;
    bvnControl.clearValidators();
    bvnControl.updateValueAndValidity();
    this.change.detectChanges();
  }

  setBvnValidators() {
    const bvnControl = this.emailForm.get('bvn')!;
    bvnControl.setValidators([Validators.required, CustomValidators.bvn()]);
    bvnControl.updateValueAndValidity();
    this.change.detectChanges();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
